/**
 * Callings (classes) and abilities. Pure data; the server executes abilities, the client mirrors
 * cooldowns and renders effects. Ability slots map to Buttons.Ability1/2/3 (Q / F / X).
 */
export const CallingId = { Vanguard: 1, Warden: 2, Shade: 3, Tinker: 4 } as const;
export type CallingIdValue = (typeof CallingId)[keyof typeof CallingId];

export type AbilityKind =
  // --- the tab-target set (Phase 2b): what Vanguard and Warden are built out of ---------------
  | 'melee' // weapon-range damage on the selected hostile
  | 'spell' // ranged damage, usually with a cast bar
  | 'heal' // direct heal on a friendly
  | 'hot' // heal over time on a friendly
  | 'shield' // absorb shield on a friendly
  | 'taunt' // damage, and forces the monster's threat to the top
  | 'charge' // close the gap to the selected hostile, then hit it
  | 'resurrect' // put a dead player back on their feet where they fell
  | 'slowfield' // a patch of ground that slows and blinds what stands in it
  // --- kinds the hidden callings (Shade, Tinker) still use ------------------------------------
  | 'deploy' // spawns an entity (bulwark, sentry, beacon, fabricator, drone)
  | 'dash' // breach charge
  | 'buff' // self or party buff (bulwark, rally cry, surge, veil, silence)
  | 'aura' // area effect around the caster
  | 'ping' // recon pulse
  | 'hijack'; // take control of a Hymn

export type AbilitySlot = 0 | 1 | 2 | 3 | 4 | 5;

/**
 * Who an ability may be pointed at. The server validates this against the selected target every
 * time (`tryUseAbility`); nothing here is a client-side courtesy.
 *
 * - `self` ignores the selection entirely.
 * - `hostile` needs a living enemy in range and in line of sight.
 * - `friendly` needs a living player — **including yourself**, which is what makes a Warden able
 *   to heal with nothing selected instead of having to click their own portrait first.
 * - `party` is an area effect centred on the caster that lands on every ally in `rangeM`; it
 *   needs no selection at all.
 */
export type AbilityTarget = 'self' | 'friendly' | 'hostile' | 'party';

export interface AbilityDef {
  id: string;
  name: string;
  slot: AbilitySlot;
  kind: AbilityKind;
  cooldownMs: number;
  /**
   * Cast time in ms. **Absent means instant**, which is what all six authored abilities are.
   *
   * A cast is interrupted by taking damage and by moving, and its effect lands when the bar fills
   * rather than when the key is pressed. The field exists now because the server gate for it
   * exists now (`tryUseAbility`), so Phase 2b's Warden casts are a data change rather than a
   * protocol one — read it as `def.castMs ?? 0`.
   */
  castMs?: number;
  /**
   * Whether this ability triggers, and is blocked by, the global cooldown
   * (`GLOBAL_COOLDOWN_MS`). **Absent means true**, which is what every ability wants unless it is
   * a defensive or an interrupt — read it as `def.gcd !== false`.
   */
  gcd?: boolean;
  durationMs: number;
  /** Ultimates cost stamina too. */
  staminaCost: number;
  /** Radius in metres where relevant. */
  radius: number;
  /** Entity kind spawned for deploy abilities (protocol.EntityKind). */
  spawnsKind?: number;
  desc: string;

  // --- Phase 2b: the tab-target contract -------------------------------------------------------
  /**
   * How far the target may be, in metres. **Absent means "the caster's own reach"** — the
   * auto-attack range from `computeStats`, which is melee for a Vanguard and the lantern's 30 m
   * for a Warden. Read it as `def.rangeM ?? stats.autoAttackRangeM`.
   */
  rangeM?: number;
  /** What the ability may be pointed at. **Absent means `hostile`**. */
  target?: AbilityTarget;
  /**
   * Rage (Vanguard) or mana (Warden) spent. **Absent means free.** Separate from `staminaCost`,
   * which survives from the shooter and still gates the two ultimates.
   */
  resourceCost?: number;
  /**
   * Damage, healing or absorb per point of the caster's attack power (melee/charge kinds) or
   * spell power (everything else). One multiply, in `abilityAmount` — see `stats.ts`.
   *
   * For a `hot` this is the coefficient of **one tick**, not of the whole duration.
   */
  coefficient?: number;
  /** `hot` only: milliseconds between ticks. */
  tickMs?: number;
}

export interface CallingDef {
  id: CallingIdValue;
  name: string;
  tagline: string;
  passiveDesc: string;
  /**
   * Not offered at character creation, but still authored, still compiled and still playable by
   * anyone who already has one. Shade and Tinker are the slice's cut classes (`docs/PLAN.md`
   * Phase 4): deleting them would take their abilities, their passives and everything keyed off
   * `CallingId` with them, and hiding them costs one boolean.
   */
  hidden?: boolean;
  /** The resource its abilities are paid for in. Absent for a calling that has none yet. */
  resource?: ResourceKind;
  /** Passive stat modifiers applied at all times. */
  passive: Partial<import('./skills.js').PlayerModifiers>;
  /**
   * Six slots, mapped to keys 1-6 and to `Buttons.Ability1..6`. Slots 3..5 are `undefined` until
   * Phase 2b authors them, which `abilityBySlot` and every caller already handle — the bar simply
   * draws three empty squares. `ABILITY_SLOTS` (constants.ts) is the length this should have.
   */
  abilities: readonly (AbilityDef | undefined)[];
}

/**
 * The two resources, and the two rhythms they buy.
 *
 * **Rage** starts a fight empty and is filled by being in one: it rewards a Vanguard for standing
 * in front of things, and it means the tank's opening rotation is the same every pull. It does
 * not regenerate; it is *earned*, and it drains away once nobody is hitting anybody.
 *
 * **Mana** starts full and only goes down, which is what makes a Warden's fight a budget rather
 * than a rotation — the healer who spends it all in the first thirty seconds is the reason the
 * party wipes at ninety. It comes back slowly on its own and quickly while sitting, which is what
 * puts a group on the ground between pulls, which is where people talk to each other.
 */
export const ResourceKind = { None: 0, Rage: 1, Mana: 2 } as const;
export type ResourceKind = (typeof ResourceKind)[keyof typeof ResourceKind];

/** Rage gained per auto-attack that lands, and per this many points of damage taken. */
export const RAGE_PER_SWING = 8;
export const RAGE_PER_DAMAGE_TAKEN = 8;
/** Rage bled off per second once out of combat: a fight's worth of rage does not keep. */
export const RAGE_DECAY_PER_S = 4;
/** Mana regenerated per second: in combat, out of combat, and sitting down. */
export const MANA_REGEN_IN_COMBAT_PER_S = 5;
export const MANA_REGEN_PER_S = 15;
export const MANA_REGEN_SITTING_PER_S = 30;

export const ENTITY_KIND_BULWARK = 30;
export const ENTITY_KIND_SENTRY = 31;
export const ENTITY_KIND_DRONE = 32;
export const ENTITY_KIND_BEACON = 33;
export const ENTITY_KIND_FABRICATOR = 34;

export const CALLINGS: Record<number, CallingDef> = {
  [CallingId.Vanguard]: {
    id: CallingId.Vanguard,
    name: 'Vanguard',
    tagline: 'The wall between them and you.',
    passiveDesc: '+20 max HP. Sexton staggers hit 25% softer. Rage builds as you fight.',
    resource: ResourceKind.Rage,
    passive: { maxHp: 20, staggerResist: 0.25 },
    abilities: [
      {
        id: 'strike',
        name: 'Strike',
        slot: 0,
        kind: 'melee',
        cooldownMs: 0,
        durationMs: 0,
        staminaCost: 0,
        radius: 0,
        resourceCost: 15,
        coefficient: 1.6,
        target: 'hostile',
        desc: 'A heavy overhand on your target. Costs 15 rage; the rhythm everything else sits on.',
      },
      {
        id: 'shieldslam',
        name: 'Shield Slam',
        slot: 1,
        kind: 'taunt',
        cooldownMs: 8000,
        durationMs: 0,
        staminaCost: 0,
        radius: 0,
        resourceCost: 10,
        coefficient: 1.0,
        target: 'hostile',
        desc: 'Slam the boss in the teeth. It hits you back, and only you, from here on.',
      },
      {
        id: 'cleave',
        name: 'Cleave',
        slot: 2,
        kind: 'melee',
        cooldownMs: 6000,
        durationMs: 0,
        staminaCost: 0,
        radius: 6,
        resourceCost: 20,
        coefficient: 1.0,
        target: 'hostile',
        desc: 'One swing through your target and everything within 6 m of it.',
      },
      {
        id: 'bulwark',
        name: 'Bulwark',
        slot: 3,
        kind: 'buff',
        cooldownMs: 30000,
        durationMs: 8000,
        staminaCost: 0,
        radius: 0,
        target: 'self',
        desc: 'Set your feet. 8 s: everything hits you 40% softer.',
      },
      {
        id: 'breach',
        name: 'Breach',
        slot: 4,
        kind: 'charge',
        cooldownMs: 20000,
        durationMs: 400,
        staminaCost: 10,
        radius: 2,
        rangeM: 25,
        coefficient: 0.8,
        target: 'hostile',
        desc: 'Close the ground to your target at a run and put your shoulder through it.',
      },
      {
        id: 'rally',
        name: 'Rally Cry',
        slot: 5,
        kind: 'buff',
        cooldownMs: 90000,
        durationMs: 15000,
        staminaCost: 20,
        radius: 20,
        resourceCost: 25,
        target: 'party',
        desc: 'Everyone within 20 m hits 15% harder and takes 10% less, for 15 s.',
      },
    ],
  },
  [CallingId.Warden]: {
    id: CallingId.Warden,
    name: 'Warden',
    tagline: 'The lantern that brings you home.',
    passiveDesc: 'Healing works 25% better. Revives are 40% faster. Mana returns fastest sitting.',
    resource: ResourceKind.Mana,
    passive: { healMult: 0.25, reviveSpeed: 0.4 },
    abilities: [
      {
        id: 'lanternbolt',
        name: 'Lantern Bolt',
        slot: 0,
        kind: 'spell',
        cooldownMs: 0,
        castMs: 1500,
        durationMs: 0,
        staminaCost: 0,
        radius: 0,
        rangeM: 30,
        resourceCost: 14,
        coefficient: 1.5,
        target: 'hostile',
        desc: 'A hard white bolt off the lantern. 1.5 s; moving or being hit puts it out.',
      },
      {
        id: 'mend',
        name: 'Mend',
        slot: 1,
        kind: 'heal',
        cooldownMs: 0,
        castMs: 2000,
        durationMs: 0,
        staminaCost: 0,
        radius: 0,
        rangeM: 30,
        resourceCost: 28,
        coefficient: 2.2,
        target: 'friendly',
        desc: 'The big heal. 2 s of standing still, and whoever you are looking at gets up.',
      },
      {
        id: 'ward',
        name: 'Ward',
        slot: 2,
        kind: 'shield',
        cooldownMs: 12000,
        durationMs: 15000,
        staminaCost: 0,
        radius: 0,
        rangeM: 30,
        resourceCost: 24,
        coefficient: 1.8,
        target: 'friendly',
        desc: 'A skin of light that soaks the next blows outright, for 15 s.',
      },
      {
        id: 'kindle',
        name: 'Kindle',
        slot: 3,
        kind: 'hot',
        cooldownMs: 0,
        durationMs: 12000,
        tickMs: 3000,
        staminaCost: 0,
        radius: 0,
        rangeM: 30,
        resourceCost: 20,
        coefficient: 0.7,
        target: 'friendly',
        desc: 'A slow warmth. Heals every 3 s for 12 s, and you can walk while it works.',
      },
      {
        id: 'beacon',
        name: 'Beacon',
        slot: 4,
        kind: 'resurrect',
        cooldownMs: 180000,
        castMs: 4000,
        durationMs: 0,
        staminaCost: 0,
        radius: 0,
        rangeM: 20,
        resourceCost: 60,
        target: 'friendly',
        desc: 'Four seconds of light over a body, and they stand up where they fell.',
      },
      {
        id: 'suppress',
        name: 'Suppression',
        slot: 5,
        kind: 'slowfield',
        cooldownMs: 30000,
        durationMs: 8000,
        staminaCost: 20,
        radius: 8,
        rangeM: 25,
        resourceCost: 35,
        target: 'hostile',
        desc: 'A dome of still light on your target. The dead inside lose you and slow to a walk.',
      },
    ],
  },
  [CallingId.Shade]: {
    id: CallingId.Shade,
    name: 'Shade',
    hidden: true,
    tagline: 'The ghost.',
    passiveDesc: 'Half as loud to the dead. Sprint costs 20% less.',
    passive: { noiseMult: -0.5, sprintDrainMult: -0.2 },
    abilities: [
      {
        id: 'veil',
        name: 'Veil',
        slot: 0,
        kind: 'buff',
        cooldownMs: 12000,
        durationMs: 5000,
        staminaCost: 0,
        radius: 0,
        desc: '5 s near-invisibility. The dead barely see you; other Wakers see a faint shimmer.',
      },
      {
        id: 'pulse',
        name: 'Pulse',
        slot: 1,
        kind: 'ping',
        cooldownMs: 25000,
        durationMs: 4000,
        staminaCost: 0,
        radius: 40,
        desc: 'Reveal the dead and Wakers within 40 m through walls for 4 s, for your party.',
      },
      {
        id: 'silence',
        name: 'Silence',
        slot: 2,
        kind: 'buff',
        cooldownMs: 60000,
        durationMs: 8000,
        staminaCost: 40,
        radius: 0,
        desc: '8 s: every blow falls silent, killing strikes land harder.',
      },
    ],
  },
  [CallingId.Tinker]: {
    id: CallingId.Tinker,
    name: 'Tinker',
    hidden: true,
    tagline: 'The engineer.',
    passiveDesc: 'Charms are 15% stronger. One extra quick slot.',
    passive: { modEffectMult: 0.15, quickSlots: 1 },
    abilities: [
      {
        id: 'sentry',
        name: 'Sentry',
        slot: 0,
        kind: 'deploy',
        cooldownMs: 12000,
        durationMs: 20000,
        staminaCost: 0,
        radius: 25,
        spawnsKind: ENTITY_KIND_SENTRY,
        desc: 'A clockwork sentinel (120 HP) covers you for 20 s.',
      },
      {
        id: 'hijack',
        name: 'Hijack',
        slot: 1,
        kind: 'hijack',
        cooldownMs: 25000,
        durationMs: 12000,
        staminaCost: 0,
        radius: 12,
        desc: 'Bind one of the dead within 12 m. It fights for you for 12 s, then falls still.',
      },
      {
        id: 'fabricator',
        name: 'Fabricator',
        slot: 2,
        kind: 'deploy',
        cooldownMs: 60000,
        durationMs: 10000,
        staminaCost: 40,
        radius: 5,
        spawnsKind: ENTITY_KIND_FABRICATOR,
        desc: 'Drop a station: your party mends and gains a draught over 10 s.',
      },
      {
        id: 'ironward',
        name: 'Iron Ward',
        slot: 3,
        kind: 'deploy',
        cooldownMs: 12000,
        durationMs: 12000,
        staminaCost: 0,
        radius: 1.5,
        target: 'self',
        spawnsKind: ENTITY_KIND_BULWARK,
        desc: 'Raise a low iron ward in front of you for 12 s (400 HP).',
      },
    ],
  },
};

export const abilityBySlot = (calling: number, slot: number): AbilityDef | undefined =>
  CALLINGS[calling]?.abilities[slot];

/** The callings a new character may choose. Hidden ones stay in `CALLINGS` and keep working. */
export const SELECTABLE_CALLINGS: readonly CallingDef[] = Object.values(CALLINGS).filter(
  (c) => !c.hidden,
);

export const resourceOf = (calling: number): ResourceKind =>
  CALLINGS[calling]?.resource ?? ResourceKind.None;

/**
 * Kinds that place, buff or summon rather than being pointed at somebody. They default to `self`
 * because they need no selection at all — the shooter-era abilities the two hidden callings are
 * still built out of are all of this shape, and requiring them to name a target they never had
 * would break every one of them.
 */
const UNTARGETED_KINDS: ReadonlySet<AbilityKind> = new Set<AbilityKind>([
  'deploy',
  'dash',
  'buff',
  'aura',
  'ping',
  'hijack',
]);

/**
 * What an ability may be pointed at, with the documented default applied once.
 *
 * The default is by kind rather than a blanket `hostile`: a Strike without a target is a mis-press
 * and should be refused, but a Sentry without a target is an ordinary deploy.
 */
export const abilityTarget = (def: AbilityDef): AbilityTarget =>
  def.target ?? (UNTARGETED_KINDS.has(def.kind) ? 'self' : 'hostile');

/** Downed state tuning (raid/expedition only). */
export const DOWNED_DURATION_MS = 20000;
export const REVIVE_SECONDS = 3;
export const REVIVE_RANGE = 2.5;
